import {
  format,
  addDays,
  parseISO,
  endOfWeek,
  endOfMonth,
  startOfWeek,
  startOfMonth,
} from 'date-fns';
import type { ComboBoxOption } from '@/components';

export interface CalendarDateRange {
  startDate: string;
  endDate: string;
}

/**
 * Calculate the start and end dates for a given month and year
 * @param [year] - The year of the month to calculate (defaults to current year)
 * @param [month] - The month to calculate (0-11, defaults to current month)
 * @returns {CalendarDateRange} - An object containing the start and end dates of the month in the format 'yyyy-MM-dd'
 */
export const calculateCalendarRange = (
  year?: number,
  month?: number
): CalendarDateRange => {
  const today = new Date();
  const date = new Date(year ?? today.getFullYear(), month ?? today.getMonth());

  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);

  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  return {
    startDate: format(calendarStart, 'yyyy-MM-dd'),
    endDate: format(calendarEnd, 'yyyy-MM-dd'),
  };
};

export interface CalendarDateSpan {
  start: string;
  endExclusive: string;
}

/**
 * Collapse a list of `yyyy-MM-dd` dates into contiguous spans.
 *
 * The calendar API returns leaves expanded one record per leave date, keyed by
 * date, so every date of a multi-day request arrives as its own record sharing
 * a `leaveRequestId` — grouping those records by that id (as
 * `useLeaveCalendarBase` does) yields the date list this takes. Collapsing it
 * lets one request render as one spanning event instead of one badge per day.
 *
 * Each span is `[start, endExclusive)` — the shape FullCalendar expects, where
 * an all-day event's `end` is exclusive. Dates the API leaves out (weekends and
 * holidays are never leave dates) split the list into separate spans, so a
 * badge is never painted over a day the employee isn't actually on leave.
 *
 * @param dates - Leave dates in `yyyy-MM-dd` format, in any order.
 * @returns The contiguous spans covering those dates, in chronological order.
 */
export const toContiguousDateSpans = (dates: string[]): CalendarDateSpan[] => {
  const spans: CalendarDateSpan[] = [];

  for (const date of Array.from(new Set(dates)).sort()) {
    const nextDay = format(addDays(parseISO(date), 1), 'yyyy-MM-dd');
    const previous = spans[spans.length - 1];

    // `endExclusive` is the day after the span, so it equals `date` exactly
    // when this date continues the run without a gap.
    if (previous?.endExclusive === date) {
      previous.endExclusive = nextDay;
      continue;
    }

    spans.push({ start: date, endExclusive: nextDay });
  }

  return spans;
};

const CALENDAR_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Parse a calendar date (a day with no time or zone, e.g. a survey date
 * answer) into local midnight of that same day.
 *
 * `new Date('2026-10-15')` reads the string as UTC midnight, which is the
 * evening of 14 October anywhere west of UTC, so formatting it shows the day
 * before. This reads only the leading `yyyy-MM-dd`, so a value that arrives
 * as a full UTC timestamp still keeps the day it names.
 *
 * @param value - A `yyyy-MM-dd` string, optionally followed by a time.
 * @returns Local midnight of that day, or `undefined` when there is no date.
 */
export const parseCalendarDate = (value: unknown): Date | undefined => {
  if (typeof value !== 'string') return undefined;
  const match = CALENDAR_DATE_PREFIX.exec(value.trim());
  if (!match) return undefined;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.getDate() === Number(day) ? date : undefined;
};

/**
 * The `yyyy-MM-dd` calendar date a picked `Date` shows in the local zone.
 * Use it instead of `toISOString()`, which shifts to UTC and so lands on the
 * previous day anywhere east of UTC.
 */
export const toCalendarDateString = (date: Date): string =>
  format(date, 'yyyy-MM-dd');

const leaveBadgePrefix = 'leave-badge';

/**
 * Map of reserved leave types to their corresponding CSS classes
 */
export const RESERVED_LEAVE_CLASSES = {
  'sick leave': `${leaveBadgePrefix} leave-badge--sick`,
  vacation: `${leaveBadgePrefix} leave-badge--vacation`,
  'casual leave': `${leaveBadgePrefix} leave-badge--casual`,
  holiday: `${leaveBadgePrefix} leave-badge--holiday`,
  nonWorkingDay: `${leaveBadgePrefix} leave-badge--non-working-day`,
};

/**
 * Array of CSS classes for circular auto leave types
 */
const AUTO_CLASSES = Array.from(
  { length: 7 },
  (_, i) => `${leaveBadgePrefix} leave-badge--auto-${i}`
);

/**
 * Maps company leave types to their corresponding CSS classes for calendar
 * The mapping rules are as follows:
 * 1. If the leave type name is in the RESERVED_LEAVE_CLASSES map,
 *    use the corresponding CSS class from that map.
 * 2. If the leave type name is not in the RESERVED_LEAVE_CLASSES map,
 *    assign a circular auto CSS class from AUTO_CLASSES array.
 * @param leaveTypes - The list of leave types
 * @returns - A map of leave type IDs to their CSS classes
 */
export function getLeaveTypesClassesMap(
  leaveTypes: ComboBoxOption[]
): Map<string, string> {
  const map = new Map<string, string>();
  let autoIndex = 0;

  for (const leaveType of leaveTypes) {
    const normalizedName = leaveType.label
      .trim()
      .toLowerCase() as keyof typeof RESERVED_LEAVE_CLASSES;

    // 2. Reserved mapping
    const reservedClass = RESERVED_LEAVE_CLASSES[normalizedName];
    if (reservedClass) {
      map.set(leaveType.value, reservedClass);
      continue;
    }

    // 3. Circular auto assignment
    const autoClass = AUTO_CLASSES[autoIndex];
    map.set(leaveType.value, autoClass);

    autoIndex = (autoIndex + 1) % AUTO_CLASSES.length;
  }

  return map;
}
